import { useState, type FormEvent } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';
import { LegacyBadges } from '../../shared/ui/LegacyBadges';

type Pastor = { id: string; pastorName: string; spouseName: string | null; startedOn: string; endedOn: string | null; photoUrl?: string | null; personId?: string | null; founder?: boolean; deceased?: boolean; deceasedYear?: number | null };
type StoryMember = { id: string; personId: string | null; name: string; founder: boolean; deceased: boolean; deceasedYear: number | null };
type Person = { personId: string; givenName: string; familyName: string };
type Photo = { id: string; caption: string | null; url: string };
type Story = { foundedOn: string | null; foundedYear: number | null; history: string | null; mission: string | null; vision: string | null; pastors: Pastor[]; members?: StoryMember[]; photos?: Photo[]; canEdit: boolean };

const LONG = new Intl.DateTimeFormat('es-MX', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
const longDate = (iso: string) => LONG.format(new Date(`${iso}T00:00:00Z`));
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Años cumplidos entre dos fechas AAAA-MM-DD (cuenta el aniversario hasta que llega). */
export function yearsBetween(from: string, to: string): number {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  return ty - fy - (tm < fm || (tm === fm && td < fd) ? 1 : 0);
}
const years = (n: number) => (n === 1 ? '1 año' : `${n} años`);

/** Cuánto lleva o llevó un pastorado, en palabras. */
function tenure(p: Pastor, today: string): string {
  const n = yearsBetween(p.startedOn, p.endedOn ?? today);
  return n < 1 ? 'Menos de un año' : years(n);
}

/** «⋯» de cada pastor: lo que no es «Editar» (cambiar o quitar la foto, quitarlo de la lista). */
function PastorMenu({ pastor: p, busy, onPhoto, onRemovePhoto, onRemove }: { pastor: Pastor; busy: boolean; onPhoto: (f: File) => void; onRemovePhoto: () => void; onRemove: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="room-menu">
      <Button variant="ghost" aria-expanded={open} aria-label={`Más opciones de ${p.pastorName}`} disabled={busy} onClick={() => setOpen((o) => !o)}>
        ⋯
      </Button>
      {open ? (
        <span className="room-menu__list room-menu__list--right" role="menu">
          <label className="btn btn--ghost" role="menuitem">
            {p.photoUrl ? 'Cambiar foto' : 'Poner foto'}
            <input
              type="file"
              accept="image/*"
              className="visually-hidden"
              disabled={busy}
              aria-label={`Foto de ${p.pastorName}`}
              onChange={(e) => {
                const f = e.target.files?.[0];
                e.target.value = '';
                setOpen(false);
                if (f) onPhoto(f);
              }}
            />
          </label>
          {p.photoUrl ? (
            <Button variant="ghost" role="menuitem" aria-label={`Quitar la foto de ${p.pastorName}`} onClick={() => { setOpen(false); onRemovePhoto(); }}>
              Quitar foto
            </Button>
          ) : null}
          <Button variant="ghost" role="menuitem" aria-label={`Quitar a ${p.pastorName}`} onClick={() => { setOpen(false); onRemove(); }}>
            Quitar de la lista
          </Button>
        </span>
      ) : null}
    </span>
  );
}

/** Mi iglesia → Nuestra historia: fundación (los años se calculan solos), misión, visión, historia y pastores. */
export function ChurchStoryPage() {
  const me = useMe();
  const churchId = me.activeChurchId;
  const base = `/multichurch/churches/${churchId}`;
  const story = useAsync((signal) => (churchId ? api.get<Story>(`${base}/story`, signal) : Promise.resolve(null)), [base]);
  const [editing, setEditing] = useState(false);
  const [pastorForm, setPastorForm] = useState<Pastor | 'new' | null>(null);
  const [memberForm, setMemberForm] = useState<StoryMember | 'new' | null>(null);
  // Para ligar a un pastor o miembro con su ficha (solo quien edita la historia).
  const people = useAsync(
    (signal) => (churchId && story.status === 'ready' && story.data?.canEdit ? api.get<{ items: Person[] }>(`/multichurch/churches/${churchId}/members`, signal).then((r) => r.items).catch(() => []) : Promise.resolve([] as Person[])),
    [churchId, story.status],
  );
  const peopleList = people.status === 'ready' ? people.data : [];
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  if (!churchId) return <EmptyState title="Elige primero una iglesia." />;
  if (story.status === 'loading') return <Loading />;
  if (story.status === 'error') return <ErrorState error={story.error} onRetry={story.reload} />;
  const s = story.data!;
  const today = todayIso();

  const run = async (action: () => Promise<unknown>, ok: string) => {
    setBusy(true);
    setMsg(null);
    try {
      await action();
      setMsg({ tone: 'success', text: ok });
      story.reload();
      return true;
    } catch (e) {
      setMsg({ tone: 'error', text: errorMessage(e) });
      return false;
    } finally {
      setBusy(false);
    }
  };

  function saveStory(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    void run(() => api.put(`${base}/story`, { foundedOn: t('foundedOn'), mission: t('mission'), vision: t('vision'), history: t('history') }), 'Historia guardada.').then((ok) => ok && setEditing(false));
  }

  function savePastor(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const t = (k: string) => String(f.get(k) ?? '').trim() || null;
    const year = t('deceasedYear');
    const body = {
      pastorName: t('pastorName'), spouseName: t('spouseName'), startedOn: t('startedOn'), endedOn: f.get('current') ? null : t('endedOn'),
      personId: t('personId'), founder: f.get('founder') === 'on', deceased: f.get('deceased') === 'on', deceasedYear: year ? Number(year) : null,
    };
    const editingId = pastorForm !== 'new' && pastorForm ? pastorForm.id : null;
    void run(() => (editingId ? api.put(`${base}/pastors/${editingId}`, body) : api.post(`${base}/pastors`, body)), 'Pastor guardado.').then((ok) => ok && setPastorForm(null));
  }

  const upload = (path: string, file: File) => api.upload(path, file, { 'x-file-name': encodeURIComponent(file.name) });
  const photos = s.photos ?? [];
  async function addPhotos(files: FileList | null) {
    const list = [...(files ?? [])];
    if (list.length) await run(async () => {
      for (const f of list) await upload(`${base}/story/photos`, f);
    }, list.length === 1 ? 'Foto agregada.' : `${list.length} fotos agregadas.`);
  }

  const founded = s.foundedOn ? yearsBetween(s.foundedOn, today) : s.foundedYear ? Number(today.slice(0, 4)) - s.foundedYear : null;
  const anniversary = s.foundedOn && s.foundedOn.slice(5) === today.slice(5) && founded;
  const empty = !s.foundedOn && !s.foundedYear && !s.history && !s.mission && !s.vision && s.pastors.length === 0 && photos.length === 0;

  return (
    <div className="page page--narrow">
      <header className="page__header page__header--actions">
        <div>
          <h1>Nuestra historia</h1>
          {me.activeContext ? <p className="muted">{me.activeContext.churchName}</p> : null}
        </div>
        {s.canEdit && !editing ? (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Editar
          </Button>
        ) : null}
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}

      {editing ? (
        <Card title="Editar historia">
          <form className="form" onSubmit={saveStory}>
            <Field label="Fecha de fundación" name="foundedOn" type="date" max={today} defaultValue={s.foundedOn ?? ''} hint="Los años cumplidos se calculan solos." />
            <div className="field">
              <label htmlFor="story-mission">Misión</label>
              <textarea id="story-mission" name="mission" rows={3} maxLength={2000} defaultValue={s.mission ?? ''} />
            </div>
            <div className="field">
              <label htmlFor="story-vision">Visión</label>
              <textarea id="story-vision" name="vision" rows={3} maxLength={2000} defaultValue={s.vision ?? ''} />
            </div>
            <div className="field">
              <label htmlFor="story-history">Historia de la iglesia</label>
              <textarea id="story-history" name="history" rows={10} maxLength={20000} defaultValue={s.history ?? ''} />
            </div>
            <div className="actions">
              <Button type="submit" busy={busy}>
                Guardar
              </Button>
              <Button type="button" variant="ghost" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </div>
          </form>
        </Card>
      ) : (
        <>
          {empty ? (
            <EmptyState title="Todavía no hay historia escrita.">{s.canEdit ? <p className="muted">Toca «Editar» para escribirla.</p> : null}</EmptyState>
          ) : null}
          {founded !== null ? (
            <Card title="Fundación">
              <p>
                <strong className="story-years">{anniversary ? `¡Hoy cumple ${years(founded)}!` : years(founded)}</strong>
              </p>
              <p className="muted">{s.foundedOn ? `Fundada el ${longDate(s.foundedOn)}.` : `Fundada en ${s.foundedYear}.`}</p>
            </Card>
          ) : null}
          {s.mission ? (
            <Card title="Misión">
              <p className="pre-line">{s.mission}</p>
            </Card>
          ) : null}
          {s.vision ? (
            <Card title="Visión">
              <p className="pre-line">{s.vision}</p>
            </Card>
          ) : null}
          {s.history ? (
            <Card title="Historia">
              <p className="pre-line">{s.history}</p>
            </Card>
          ) : null}
          {photos.length > 0 || s.canEdit ? (
            <Card
              title="Fotos"
              action={
                s.canEdit ? (
                  <label className="btn btn--ghost">
                    + Subir fotos
                    <input type="file" accept="image/*" multiple className="visually-hidden" disabled={busy} aria-label="Subir fotos de la historia" onChange={(e) => { void addPhotos(e.target.files); e.target.value = ''; }} />
                  </label>
                ) : null
              }
            >
              {photos.length === 0 ? <p className="muted">Sube fotos del templo, de los primeros años y de los momentos importantes.</p> : null}
              <ul className="photo-grid story-photos" aria-label="Fotos de la historia">
                {photos.map((ph) => (
                  <li key={ph.id}>
                    <figure>
                      <a href={ph.url} target="_blank" rel="noopener noreferrer">
                        <img src={ph.url} alt={ph.caption ?? 'Foto de la historia'} loading="lazy" />
                      </a>
                      {ph.caption ? <figcaption className="small">{ph.caption}</figcaption> : null}
                    </figure>
                    {s.canEdit ? (
                      <span className="actions">
                        <Button
                          variant="ghost"
                          disabled={busy}
                          aria-label={`Pie de foto${ph.caption ? ` de ${ph.caption}` : ''}`}
                          onClick={() => {
                            const caption = window.prompt('Pie de foto (qué es y de qué año):', ph.caption ?? '');
                            if (caption !== null) void run(() => api.put(`${base}/story/photos/${ph.id}`, { caption: caption.trim() || null }), 'Pie de foto guardado.');
                          }}
                        >
                          Pie
                        </Button>
                        <Button
                          variant="ghost"
                          disabled={busy}
                          aria-label={`Quitar foto${ph.caption ? ` ${ph.caption}` : ''}`}
                          onClick={() => {
                            if (window.confirm('¿Quitar esta foto de la historia?')) void run(() => api.delete(`${base}/story/photos/${ph.id}`), 'Foto quitada.');
                          }}
                        >
                          Quitar
                        </Button>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </>
      )}

      {s.pastors.length > 0 || s.canEdit ? (
        <Card
          title="Pastores"
          action={
            s.canEdit && !pastorForm ? (
              <Button variant="ghost" onClick={() => setPastorForm('new')}>
                + Agregar
              </Button>
            ) : null
          }
        >
          {pastorForm ? (
            <form className="form" onSubmit={savePastor} key={pastorForm === 'new' ? 'new' : pastorForm.id}>
              <PastorFields pastor={pastorForm === 'new' ? null : pastorForm} today={today} people={peopleList} />
              <div className="actions">
                <Button type="submit" busy={busy}>
                  Guardar
                </Button>
                <Button type="button" variant="ghost" onClick={() => setPastorForm(null)}>
                  Cancelar
                </Button>
              </div>
            </form>
          ) : null}
          {s.pastors.length === 0 ? (
            <p className="muted">Agrega a los pastores, del primero al actual.</p>
          ) : (
            <ol className="list" aria-label="Pastores">
              {s.pastors.map((p) => (
                <li key={p.id} className="list__item list__item--actions">
                  {p.photoUrl ? <img className="avatar" src={p.photoUrl} alt={`Foto de ${p.pastorName}`} width={56} height={56} /> : null}
                  <span className="agenda__body">
                    <strong>
                      {p.pastorName}
                      {p.spouseName ? ` y ${p.spouseName}` : ''}
                      <LegacyBadges founder={p.founder ? 'pastor' : null} deceased={p.deceased} deceasedYear={p.deceasedYear} />
                    </strong>
                    <span className="small muted">
                      {longDate(p.startedOn)} – {p.endedOn ? longDate(p.endedOn) : 'actual'} · {tenure(p, today)}
                    </span>
                  </span>
                  {p.endedOn ? null : <span className="badge">Actual</span>}
                  {s.canEdit ? (
                    <span className="actions">
                      <Button variant="ghost" disabled={busy} onClick={() => setPastorForm(p)} aria-label={`Editar a ${p.pastorName}`}>
                        Editar
                      </Button>
                      <PastorMenu
                        pastor={p}
                        busy={busy}
                        onPhoto={(f) => void run(() => upload(`${base}/pastors/${p.id}/photo`, f), 'Foto guardada.')}
                        onRemovePhoto={() => {
                          if (window.confirm(`¿Quitar la foto de ${p.pastorName}?`)) void run(() => api.delete(`${base}/pastors/${p.id}/photo`), 'Foto quitada.');
                        }}
                        onRemove={() => {
                          if (window.confirm(`¿Quitar a ${p.pastorName} de la lista de pastores?`)) void run(() => api.delete(`${base}/pastors/${p.id}`), 'Pastor quitado.');
                        }}
                      />
                    </span>
                  ) : null}
                </li>
              ))}
            </ol>
          )}
        </Card>
      ) : null}

      {(s.members ?? []).length > 0 || s.canEdit ? (
        <Card
          title="Miembros de nuestra historia"
          action={
            s.canEdit && !memberForm ? (
              <Button variant="ghost" onClick={() => setMemberForm('new')}>
                + Agregar
              </Button>
            ) : null
          }
        >
          {memberForm ? (
            <form
              className="form"
              key={memberForm === 'new' ? 'new' : memberForm.id}
              onSubmit={(e) => {
                e.preventDefault();
                const f = new FormData(e.currentTarget);
                const t = (k: string) => String(f.get(k) ?? '').trim() || null;
                const personId = t('personId');
                const person = peopleList.find((x) => x.personId === personId);
                const year = t('deceasedYear');
                const body = { personId, name: t('name') ?? (person ? `${person.givenName} ${person.familyName}`.trim() : ''), founder: f.get('founder') === 'on', deceased: f.get('deceased') === 'on', deceasedYear: year ? Number(year) : null };
                const id = memberForm !== 'new' ? memberForm.id : null;
                void run(() => (id ? api.put(`${base}/story/members/${id}`, body) : api.post(`${base}/story/members`, body)), 'Guardado.').then((ok) => ok && setMemberForm(null));
              }}
            >
              <MarkFields people={peopleList} personId={memberForm === 'new' ? null : memberForm.personId} name={memberForm === 'new' ? '' : memberForm.name} founder={memberForm === 'new' ? true : memberForm.founder} deceased={memberForm !== 'new' && memberForm.deceased} deceasedYear={memberForm === 'new' ? null : memberForm.deceasedYear} withName />
              <div className="actions">
                <Button type="submit" busy={busy}>
                  Guardar
                </Button>
                <Button type="button" variant="ghost" onClick={() => setMemberForm(null)}>
                  Cancelar
                </Button>
              </div>
            </form>
          ) : null}
          {(s.members ?? []).length === 0 ? (
            <p className="muted">Agrega a los miembros fundadores y a los hermanos que ya partieron con el Señor.</p>
          ) : (
            <ul className="list" aria-label="Miembros de nuestra historia">
              {(s.members ?? []).map((m) => (
                <li key={m.id} className="list__item list__item--actions">
                  <span className="agenda__body">
                    <strong>
                      {m.name}
                      <LegacyBadges founder={m.founder ? 'miembro' : null} deceased={m.deceased} deceasedYear={m.deceasedYear} />
                    </strong>
                  </span>
                  {s.canEdit ? (
                    <span className="actions">
                      <Button variant="ghost" disabled={busy} onClick={() => setMemberForm(m)} aria-label={`Editar a ${m.name}`}>
                        Editar
                      </Button>
                      <Button
                        variant="ghost"
                        disabled={busy}
                        aria-label={`Quitar a ${m.name}`}
                        onClick={() => {
                          if (window.confirm(`¿Quitar a ${m.name} de la historia?`)) void run(() => api.delete(`${base}/story/members/${m.id}`), 'Quitado.');
                        }}
                      >
                        Quitar
                      </Button>
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </div>
  );
}

/** Ficha en KEORA (opcional), nombre, Fundador y Finado (con año). */
function MarkFields({ people, personId, name, founder, deceased, deceasedYear, withName = false }: { people: Person[]; personId: string | null; name?: string; founder: boolean; deceased: boolean; deceasedYear: number | null; withName?: boolean }) {
  const [isDeceased, setDeceased] = useState(deceased);
  return (
    <>
      <div className="field">
        <label htmlFor="mark-person">Su ficha en KEORA (opcional)</label>
        <select id="mark-person" name="personId" defaultValue={personId ?? ''}>
          <option value="">— Sin ficha —</option>
          {people.map((p) => (
            <option key={p.personId} value={p.personId}>
              {`${p.givenName} ${p.familyName}`.trim()}
            </option>
          ))}
        </select>
        <p className="field__hint">Si la tiene, el ícono de fundador sale también junto a su nombre en Miembros.</p>
      </div>
      {withName ? <Field label="Nombre (si no tiene ficha)" name="name" maxLength={150} defaultValue={name ?? ''} /> : null}
      <label className="check">
        <input type="checkbox" name="founder" defaultChecked={founder} /> Fundador
      </label>
      <label className="check">
        <input type="checkbox" name="deceased" checked={isDeceased} onChange={(e) => setDeceased(e.target.checked)} /> Finado
      </label>
      {isDeceased ? <Field label="Año en que partió (opcional)" name="deceasedYear" type="number" min={1800} max={2200} defaultValue={deceasedYear ?? ''} /> : null}
    </>
  );
}

function PastorFields({ pastor, today, people }: { pastor: Pastor | null; today: string; people: Person[] }) {
  const [current, setCurrent] = useState(pastor ? pastor.endedOn === null : true);
  return (
    <>
      <Field label="Pastor" name="pastorName" required minLength={2} maxLength={120} defaultValue={pastor?.pastorName ?? ''} />
      <Field label="Esposa" name="spouseName" maxLength={120} defaultValue={pastor?.spouseName ?? ''} hint="Opcional." />
      <Field label="Fecha de inicio" name="startedOn" type="date" required max={today} defaultValue={pastor?.startedOn ?? ''} />
      <label className="check">
        <input type="checkbox" name="current" checked={current} onChange={(e) => setCurrent(e.target.checked)} /> Es el pastor actual
      </label>
      {current ? null : <Field label="Fecha de fin" name="endedOn" type="date" required max={today} defaultValue={pastor?.endedOn ?? ''} />}
      <MarkFields people={people} personId={pastor?.personId ?? null} founder={pastor?.founder ?? false} deceased={pastor?.deceased ?? false} deceasedYear={pastor?.deceasedYear ?? null} />
    </>
  );
}
